import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Dropdown, message, Spin } from 'antd';
import copy from 'copy-to-clipboard';
import { getScopedProviderOptions } from 'api/group';
import { TScopedProviderOption } from 'types/group';
import { generateKlarityReviewLink } from 'utils/common';
import s from './s.module.less';

type Props = {
    // group practice admin sees a provider picker before copying the link
    showProviderPicker?: boolean;
    // current user's public uid, used to build his/her own survey link
    uidPublic?: string;
    className?: string;
    // lets the page anchor the first-run coachmark on this button
    triggerRef?: React.Ref<HTMLElement>;
    // copying is only half the job — the page follows up with the "how to use it" guide
    onCopied?: (providerId?: number) => void;
};

const COPIED_RESET_DELAY = 1500;

const getInitials = (name: string) =>
    name
        .split(' ')
        .filter(Boolean)
        .map((n) => n.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);

const getOptionName = (option: TScopedProviderOption) =>
    `${option.firstName || ''} ${option.lastName || ''}`.trim() || 'Unnamed provider';

const SearchIcon = () => (
    <svg fill="none" viewBox="0 0 16 16" stroke="currentColor" strokeWidth="2">
        <circle cx="7" cy="7" r="5" />
        <line x1="11" y1="11" x2="14" y2="14" />
    </svg>
);

const CheckIcon = ({ className }: { className?: string }) => (
    <svg
        className={className}
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <polyline points="3 8.5 6.5 12 13 4" />
    </svg>
);

const CopyIcon = () => (
    <svg
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <rect x="5" y="5" width="9" height="9" rx="1.5" />
        <path d="M3 11V3a2 2 0 012-2h8" />
    </svg>
);

const ArrowIcon = () => (
    <svg
        className={s.arrow}
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <polyline points="4 6 8 10 12 6" />
    </svg>
);

const CollectReviewsButton: React.FC<Props> = ({ showProviderPicker, uidPublic, className, triggerRef, onCopied }) => {
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState<TScopedProviderOption[]>([]);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [keyword, setKeyword] = useState('');
    const [selectedId, setSelectedId] = useState<number>();
    const [copied, setCopied] = useState(false);

    const ownLink = generateKlarityReviewLink(uidPublic);

    const fetchOptions = useCallback(async () => {
        setLoadingOptions(true);
        try {
            const res = await getScopedProviderOptions({ includeSelf: true });
            if (res && !res.error) {
                const list: TScopedProviderOption[] = res.data?.data || [];
                // current user first, matching the prototype
                const sorted = [...list].sort((a, b) => Number(!!b.isSelf) - Number(!!a.isSelf));
                setOptions(sorted);
                setSelectedId(
                    (prev) => prev ?? sorted.find((item) => item.isSelf)?.providerId ?? sorted[0]?.providerId,
                );
            }
        } catch (e) {
            console.error(e);
        }
        setLoadingOptions(false);
    }, []);

    useEffect(() => {
        if (showProviderPicker) {
            fetchOptions();
        }
    }, [showProviderPicker, fetchOptions]);

    const filteredOptions = useMemo(() => {
        const q = keyword.trim().toLowerCase();
        if (!q) {
            return options;
        }
        return options.filter((option) => getOptionName(option).toLowerCase().includes(q));
    }, [options, keyword]);

    const resolveSurveyLink = (option: TScopedProviderOption) => {
        if (option.isSelf) {
            return ownLink;
        }
        return option.uidPublic ? generateKlarityReviewLink(option.uidPublic) : '';
    };

    const handleCopyOwnLink = () => {
        copy(ownLink || '', { format: 'text/plain' });
        message.success('Survey link copied successfully!');
        onCopied?.();
    };

    const handleCopySelected = () => {
        const option = options.find((item) => item.providerId === selectedId);
        if (!option) {
            return;
        }
        const link = resolveSurveyLink(option);
        if (!link) {
            message.error('This provider does not have a survey link yet.');
            return;
        }
        copy(link, { format: 'text/plain' });
        setCopied(true);
        setTimeout(() => {
            setCopied(false);
            setOpen(false);
            onCopied?.(option.providerId);
        }, COPIED_RESET_DELAY);
    };

    const handleOpenChange = (nextOpen: boolean) => {
        setOpen(nextOpen);
        if (nextOpen) {
            setKeyword('');
            setCopied(false);
        }
    };

    if (!showProviderPicker) {
        if (!ownLink) {
            return null;
        }
        return (
            <Button
                ref={triggerRef as React.Ref<HTMLButtonElement>}
                onClick={handleCopyOwnLink}
                type="primary"
                className={className}
            >
                Collect patient reviews
            </Button>
        );
    }

    const panel = (
        <div className={s.dropdownPanel} onClick={(e) => e.stopPropagation()}>
            <div className={s.hdr}>
                <div className={s.title}>Select a provider</div>
                <div className={s.search}>
                    <SearchIcon />
                    <input
                        type="text"
                        placeholder="Search providers..."
                        value={keyword}
                        onChange={(e) => setKeyword(e.target.value)}
                    />
                </div>
            </div>
            <Spin spinning={loadingOptions}>
                <div className={s.list}>
                    {filteredOptions.map((option) => (
                        <div
                            key={option.providerId}
                            className={`${s.item} ${option.providerId === selectedId ? s.itemSelected : ''}`}
                            onClick={() => setSelectedId(option.providerId)}
                        >
                            {option.avatar ? (
                                <img className={s.avatar} src={option.avatar} alt="" />
                            ) : (
                                <div className={s.avatar}>{getInitials(getOptionName(option))}</div>
                            )}
                            <div className={s.itemInfo}>
                                <div className={s.itemName}>
                                    {getOptionName(option)}
                                    {option.isSelf && <span className={s.itemYou}>(You)</span>}
                                </div>
                                <div className={s.itemCred}>{option.credential}</div>
                            </div>
                            <CheckIcon className={s.check} />
                        </div>
                    ))}
                    {!loadingOptions && filteredOptions.length === 0 && (
                        <div className={s.empty}>No providers found</div>
                    )}
                </div>
            </Spin>
            <div className={s.footer}>
                <button type="button" className={s.btnCancel} onClick={() => setOpen(false)}>
                    Cancel
                </button>
                <button
                    type="button"
                    className={`${s.btnCopy} ${copied ? s.btnCopied : ''}`}
                    disabled={!selectedId}
                    onClick={handleCopySelected}
                >
                    {copied ? (
                        <>
                            <CheckIcon />
                            Copied!
                        </>
                    ) : (
                        <>
                            <CopyIcon />
                            Copy link
                        </>
                    )}
                </button>
            </div>
        </div>
    );

    return (
        <Dropdown
            open={open}
            onOpenChange={handleOpenChange}
            trigger={['click']}
            placement="bottomRight"
            dropdownRender={() => panel}
        >
            <Button
                ref={triggerRef as React.Ref<HTMLButtonElement>}
                type="primary"
                className={`${className || ''} ${s.triggerBtn}`}
            >
                <span className={s.triggerText}>Collect patient reviews</span>
                <ArrowIcon />
            </Button>
        </Dropdown>
    );
};

export default CollectReviewsButton;
